import { TextInput, NumberInput, Select, Stack, Switch } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  required,
  compose,
  scrollToFirstError,
} from "../../../utils/formValidation";
import { SpecimenBatchTemplate } from "../../../services/admin/specimenMacro.service";

const MATERIAL_OPTIONS = [
  { value: "formalin_jar", label: "Formalin jar" },
  { value: "block", label: "Block" },
  { value: "slides_only", label: "Slides only" },
  { value: "blood_tube", label: "Blood tube" },
  { value: "urine_sample", label: "Urine sample" },
  { value: "stool_collection", label: "Stool collection" },
].sort((a, b) => a.label.localeCompare(b.label));

export interface BatchTemplateFormValues {
  name: string;
  specimenCount: number;
  defaultMaterial: string | null;
  isActive: boolean;
}

interface BatchTemplateFormProps {
  formId: string;
  initialValues?: SpecimenBatchTemplate;
  onSubmit: (values: BatchTemplateFormValues) => void;
}

export default function BatchTemplateForm({
  formId,
  initialValues,
  onSubmit,
}: BatchTemplateFormProps) {
  const form = useForm<BatchTemplateFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      specimenCount: initialValues?.specimenCount || 1,
      defaultMaterial: initialValues?.defaultMaterial || null,
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      name: compose(required("Name is required")),
      specimenCount: (value) => {
        if (!value || value < 1) return "Must be at least 1";
        if (value > 26) return "Must be at most 26";
        if (!Number.isInteger(value)) return "Must be a whole number";
        return null;
      },
    },
  });

  const handleSubmit = form.onSubmit(
    (values) => onSubmit(values),
    () => scrollToFirstError(form)
  );

  return (
    <form id={formId} onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder='e.g., "Formalin Jar - 9 Parts"'
          withAsterisk
          {...form.getInputProps("name")}
        />

        <NumberInput
          label="Specimen Count"
          placeholder="Number of specimens to create"
          withAsterisk
          min={1}
          max={26}
          {...form.getInputProps("specimenCount")}
        />

        <Select
          label="Default Material"
          placeholder="Select material..."
          data={MATERIAL_OPTIONS}
          clearable
          {...form.getInputProps("defaultMaterial")}
        />

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
